import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaTicketAlt, FaComments, FaPaperPlane, FaCheck, FaUserCheck, FaBuilding } from 'react-icons/fa';

const TicketDetailsModal = ({ ticket, onClose, userInfo, API_BASE_URL, auth, onUpdate }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolutionForm, setShowResolutionForm] = useState(false);

  const statusColors = {
    open: '#3498db',
    in_progress: '#f39c12',
    resolved: '#27ae60',
    closed: '#95a5a6',
    cancelled: '#e74c3c'
  };

  const priorityColors = {
    low: '#95a5a6',
    medium: '#3498db',
    high: '#f39c12',
    urgent: '#e74c3c'
  };

  useEffect(() => {
    if (ticket) {
      fetchComments();
    }
  }, [ticket]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/comments?include_internal=true`,
        auth
      );
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/comments`,
        {
          user_id: userInfo.user_id,
          comment: newComment,
          is_internal: isInternal
        },
        auth
      );

      setNewComment('');
      setIsInternal(false);
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveTicket = async () => {
    if (!resolutionNotes.trim()) {
      alert('Please provide resolution notes');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/resolve`,
        {
          resolved_by: userInfo.user_id,
          resolution_notes: resolutionNotes
        },
        auth
      );

      setShowResolutionForm(false);
      setResolutionNotes('');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error resolving ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToSelf = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/assign`,
        {
          assigned_to: userInfo.user_id,
          assigned_by: userInfo.user_id
        },
        auth
      );

      onUpdate();
    } catch (error) {
      console.error('Error assigning ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => (
    <span style={{
      backgroundColor: statusColors[status] || '#95a5a6',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    }}>
      {status.replace('_', ' ')}
    </span>
  );

  const getPriorityBadge = (priority) => (
    <span style={{
      backgroundColor: priorityColors[priority] || '#95a5a6',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    }}>
      {priority}
    </span>
  );

  if (!ticket) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          borderBottom: '1px solid #dee2e6',
          paddingBottom: '1rem'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FaTicketAlt color="#3498db" />
              Ticket #{ticket.ticket_number}
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', color: '#7f8c8d' }}>
              Created by {ticket.creator_name} on {formatDate(ticket.created_at)}
            </p>
            {ticket.mmda_name && (
              <p style={{ margin: '0.25rem 0 0 0', color: '#7f8c8d', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaBuilding size={12} />
                {ticket.mmda_name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#7f8c8d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Ticket Info */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '0.25rem'
        }}>
          <div>
            <strong>Status:</strong><br />
            {getStatusBadge(ticket.status)}
          </div>
          <div>
            <strong>Priority:</strong><br />
            {getPriorityBadge(ticket.priority)}
          </div>
          <div>
            <strong>Category:</strong><br />
            {ticket.category.replace('_', ' ')}
          </div>
          <div>
            <strong>Assigned To:</strong><br />
            {ticket.assignee_name || <span style={{ color: '#e74c3c' }}>Unassigned</span>}
          </div>
        </div>

        {/* Title and Description */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>{ticket.title}</h3>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '0.25rem',
            border: '1px solid #dee2e6'
          }}>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
          </div>
        </div>

        {/* Resolution Notes */}
        {ticket.resolution_notes && (
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#27ae60' }}>Resolution</h4>
            <div style={{
              backgroundColor: '#f0f9f0',
              padding: '1rem',
              borderRadius: '0.25rem',
              border: '1px solid #27ae60'
            }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{ticket.resolution_notes}</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#7f8c8d' }}>
                Resolved by {ticket.resolver_name} on {formatDate(ticket.resolved_at)}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '2rem',
            padding: '1rem',
            backgroundColor: '#fff3cd',
            borderRadius: '0.25rem',
            border: '1px solid #ffeaa7'
          }}>
            {!ticket.assigned_to && (
              <button
                onClick={handleAssignToSelf}
                disabled={loading}
                style={{
                  backgroundColor: '#f39c12',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaUserCheck />
                Assign to Me
              </button>
            )}
            {(ticket.assigned_to === userInfo.user_id || !ticket.assigned_to || userInfo.role_name === 'super_admin') && (
              <button
                onClick={() => setShowResolutionForm(true)}
                style={{
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaCheck />
                Resolve Ticket
              </button>
            )}
          </div>
        )}

        {/* Resolution Form */}
        {showResolutionForm && (
          <div style={{
            marginBottom: '2rem',
            padding: '1rem',
            backgroundColor: '#f0f9f0',
            borderRadius: '0.25rem',
            border: '1px solid #27ae60'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#27ae60' }}>Resolve Ticket</h4>
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Describe how this issue was resolved..."
              rows={4}
              style={{
                width: '95%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '0.25rem',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleResolveTicket}
                disabled={loading}
                style={{
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                {loading ? 'Resolving...' : 'Resolve'}
              </button>
              <button
                onClick={() => setShowResolutionForm(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaComments />
            Comments ({comments.length})
          </h4>

          {/* Comments List */}
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            borderRadius: '0.25rem',
            marginBottom: '1rem'
          }}>
            {comments.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                No comments yet
              </div>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.comment_id}
                  style={{
                    padding: '1rem',
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: comment.is_internal ? '#fff3cd' : 'white'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <strong>{comment.user_name}</strong>
                      <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                        ({comment.role_name})
                      </span>
                      {comment.is_internal && (
                        <span style={{
                          backgroundColor: '#f39c12',
                          color: 'white',
                          padding: '0.125rem 0.25rem',
                          borderRadius: '0.125rem',
                          fontSize: '0.7rem'
                        }}>
                          INTERNAL
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{comment.comment}</p>
                </div>
              ))
            )}
          </div>

          {/* Add Comment Form */}
          <form onSubmit={handleAddComment}>
            <div style={{ marginBottom: '1rem' }}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
                Internal note (only visible to staff)
              </label>
              <button
                type="submit"
                disabled={loading || !newComment.trim()}
                style={{
                  backgroundColor: loading || !newComment.trim() ? '#95a5a6' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: loading || !newComment.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaPaperPlane />
                {loading ? 'Adding...' : 'Add Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsModal;
